import React, { useState, useEffect, useCallback } from 'react';
import { Home, ChevronLeft, ChevronRight, ShieldCheck, FileText, Mail } from 'lucide-react';
import PVSPageLayout from './PVSPageLayout';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://app.pvslimited.co.uk';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const PERIODS = ['daily', 'weekly', 'monthly', 'yearly'];

const PERIOD_SUFFIX = {
  daily: 'today',
  weekly: 'this week',
  monthly: 'this month',
  yearly: 'this year',
};

const REPORT_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const londonToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
const CURRENT_YEAR = Number(londonToday().slice(0, 4));
const REPORT_YEARS = Array.from({ length: CURRENT_YEAR - 2019 }, (_, i) => CURRENT_YEAR - i);

// Report weeks run Monday to Sunday.
const reportWeekLabel = (isoDate) => {
  const start = new Date(`${isoDate}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
};

const formatDateLabel = (date, period) => {
  const d = new Date(date);
  const opts = { timeZone: 'Europe/London' };
  switch (period) {
    case 'daily':
      return d.toLocaleDateString('en-GB', { ...opts, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    case 'weekly': {
      const end = new Date(d);
      end.setDate(end.getDate() + 6);
      return `${d.toLocaleDateString('en-GB', { ...opts, day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('en-GB', { ...opts, day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    case 'monthly':
      return d.toLocaleDateString('en-GB', { ...opts, month: 'long', year: 'numeric' });
    case 'yearly':
      return d.getFullYear().toString();
    default:
      return d.toLocaleDateString('en-GB', opts);
  }
};

const shiftDate = (date, period, direction) => {
  const d = new Date(date);
  switch (period) {
    case 'daily': d.setDate(d.getDate() + direction); break;
    case 'weekly': d.setDate(d.getDate() + 7 * direction); break;
    case 'monthly': d.setMonth(d.getMonth() + direction); break;
    case 'yearly': d.setFullYear(d.getFullYear() + direction); break;
    default: break;
  }
  return d.toISOString().split('T')[0];
};

const buildAnalyticsQuery = ({ selectedOwner }) => {
  const params = new URLSearchParams();
  if (selectedOwner) params.set('owner', selectedOwner);
  const query = params.toString();
  return query ? `?${query}` : '';
};

const StatCard = ({ label, value, highlight }) => (
  <div className={`pvs-glass p-5 text-center ${highlight ? 'border-green-400/40' : ''}`}>
    <p className="text-3xl font-bold text-white mb-1">{value ?? '–'}</p>
    <p className="text-sm text-pvs-text-muted">{label}</p>
  </div>
);

const LeaderboardTable = ({ title, entries, loading, emptyLabel, countLabel, nameLabel }) => (
  <div className="space-y-2">
    <h2 className="text-lg font-semibold text-white">{title}</h2>
    <div className="pvs-glass overflow-hidden">
      {loading ? (
        <p className="text-pvs-text-muted text-sm text-center py-8">Loading...</p>
      ) : entries.length === 0 ? (
        <p className="text-pvs-text-muted text-sm text-center py-8">{emptyLabel}</p>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-pvs-text-muted text-xs uppercase tracking-wider border-b border-pvs-border">
                <th className="p-4 pr-2">#</th>
                <th className="p-4">{nameLabel}</th>
                <th className="p-4 text-right">{countLabel}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <tr key={entry.name} className="border-t border-pvs-border/50">
                  <td className="p-4 pr-2 text-pvs-text-muted font-medium">{idx + 1}</td>
                  <td className="p-4 text-white">{entry.name}</td>
                  <td className="p-4 text-right text-green-400 font-semibold">{entry.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

const CompanyLeaderboardSection = ({ company, leaderboards, loading }) => {
  const certTotal = leaderboards?.tankCertification?.reduce((sum, entry) => sum + entry.count, 0) || 0;

  return (
    <div className="space-y-4 pt-4 border-t border-pvs-border/60">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-white">{company}</h2>
        <span className="text-sm text-pvs-text-muted">{certTotal} cert{certTotal === 1 ? '' : 's'}</span>
      </div>
      <LeaderboardTable
        title="Tank Certifications"
        entries={leaderboards?.tankCertification || []}
        loading={loading}
        emptyLabel="No certifications in this period"
        nameLabel="Examiner"
        countLabel="Certs"
      />
      <LeaderboardTable
        title="Pre-Inspections"
        entries={leaderboards?.preInspection || []}
        loading={loading}
        emptyLabel="No pre-inspections in this period"
        nameLabel="Inspector"
        countLabel="Forms"
      />
      <LeaderboardTable
        title="Gas Free Checks"
        entries={leaderboards?.gasFreeCheck || []}
        loading={loading}
        emptyLabel="No gas free checks in this period"
        nameLabel="Inspector"
        countLabel="Forms"
      />
      <LeaderboardTable
        title="Final QC"
        entries={leaderboards?.finalQCChecklist || []}
        loading={loading}
        emptyLabel="No final QC checks in this period"
        nameLabel="Inspector"
        countLabel="Forms"
      />
    </div>
  );
};

const Analytics = ({ onReturnHome, isAdmin }) => {
  const [view, setView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [owners, setOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [period, setPeriod] = useState('monthly');
  const [leaderDate, setLeaderDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboards, setLeaderboards] = useState({ preInspection: [], gasFreeCheck: [], finalQCChecklist: [] });
  const [leaderboardsByOwner, setLeaderboardsByOwner] = useState({});
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [periodTotals, setPeriodTotals] = useState({ tested: 0, painted: 0, dispatched: 0 });

  const [reportOwner, setReportOwner] = useState('');
  const [reportKind, setReportKind] = useState('activity');
  const [reportType, setReportType] = useState('monthly');
  const [reportDate, setReportDate] = useState(londonToday);
  const [reportMonth, setReportMonth] = useState(() => londonToday().slice(0, 7));
  const [reportQuarter, setReportQuarter] = useState(() => Math.floor((Number(londonToday().slice(5, 7)) - 1) / 3) + 1);
  const [reportYear, setReportYear] = useState(CURRENT_YEAR);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMessage, setReportMessage] = useState(null);
  const [leaderLoading, setLeaderLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${API_BASE}/api/vessels/analytics${buildAnalyticsQuery({ selectedOwner })}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load analytics');
      const data = await res.json();
      setStats(data);
      if (data.owners) setOwners(data.owners);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOwner]);

  const fetchLeaderboard = useCallback(async () => {
    if (!isAdmin || view !== 'admin') return;
    try {
      setLeaderLoading(true);
      const params = new URLSearchParams({ period, date: leaderDate });
      if (selectedOwner) params.set('owner', selectedOwner);
      if (groupByCompany && !selectedOwner) params.set('groupByOwner', 'true');
      const url = `${API_BASE}/api/vessels/analytics/leaderboard?${params.toString()}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load leaderboard');
      const data = await res.json();
      setLeaderboard(data.leaderboard || []);
      setLeaderboards({
        preInspection: data.leaderboards?.preInspection || [],
        gasFreeCheck: data.leaderboards?.gasFreeCheck || [],
        finalQCChecklist: data.leaderboards?.finalQCChecklist || [],
      });
      setLeaderboardsByOwner(data.leaderboardsByOwner || {});
      setPeriodTotals(data.totals || { tested: 0, painted: 0, dispatched: 0 });
    } catch {
      setLeaderboard([]);
      setLeaderboards({ preInspection: [], gasFreeCheck: [], finalQCChecklist: [] });
      setLeaderboardsByOwner({});
      setPeriodTotals({ tested: 0, painted: 0, dispatched: 0 });
    } finally {
      setLeaderLoading(false);
    }
  }, [isAdmin, view, period, leaderDate, selectedOwner, groupByCompany]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // The scrap report is always weekly and Calor Gas only.
  const isScrapReport = reportKind === 'scrap';
  const effectiveReportType = isScrapReport ? 'weekly' : reportType;

  const reportRequest = () => ({
    report: reportKind,
    period: effectiveReportType,
    date: reportAnchorDate(),
    ...(!isScrapReport && reportOwner ? { owner: reportOwner } : {}),
  });

  // Any date inside the selected period; the server resolves the period boundaries.
  const reportAnchorDate = () => {
    switch (effectiveReportType) {
      case 'weekly':
        return reportDate;
      case 'quarterly':
        return `${reportYear}-${String((reportQuarter - 1) * 3 + 1).padStart(2, '0')}-01`;
      case 'annual':
        return `${reportYear}-01-01`;
      default:
        return `${reportMonth}-01`;
    }
  };

  const downloadReport = async () => {
    try {
      setReportBusy(true);
      setReportMessage(null);
      const params = new URLSearchParams(reportRequest());
      const res = await fetch(`${API_BASE}/api/reports/generate?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to generate report');
      }
      const blob = await res.blob();
      const filename = res.headers.get('X-Report-Filename') || 'PVS-Report.pdf';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setReportMessage({ type: 'success', text: `Report downloaded (${filename})` });
    } catch (err) {
      setReportMessage({ type: 'error', text: err.message });
    } finally {
      setReportBusy(false);
    }
  };

  const emailReport = async () => {
    try {
      setReportBusy(true);
      setReportMessage(null);
      const res = await fetch(`${API_BASE}/api/reports/email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(reportRequest()),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to email report');
      setReportMessage({ type: 'success', text: `Report emailed to ${body.emailedTo}` });
    } catch (err) {
      setReportMessage({ type: 'error', text: err.message });
    } finally {
      setReportBusy(false);
    }
  };

  const showGroupedByCompany = groupByCompany && !selectedOwner;
  const groupedCompanies = Object.values(leaderboardsByOwner)
    .sort((a, b) => {
      const aTotal = a.leaderboards?.tankCertification?.reduce((sum, entry) => sum + entry.count, 0) || 0;
      const bTotal = b.leaderboards?.tankCertification?.reduce((sum, entry) => sum + entry.count, 0) || 0;
      if (bTotal !== aTotal) return bTotal - aTotal;
      return a.owner.localeCompare(b.owner);
    });

  // Admin panel sub-view
  if (view === 'admin' && isAdmin) {
    return (
      <PVSPageLayout className="py-12 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex justify-between items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">Certification Leaderboard</h1>
            <button
              className="flex items-center justify-center w-12 h-12 flex-shrink-0 pvs-glass pvs-glass-hover"
              onClick={() => setView('overview')}
              title="Back to Analytics"
            >
              <ChevronLeft className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>

          <div className="pvs-glass p-4">
            <label className="block text-sm text-pvs-text-muted mb-2">Filter by Customer</label>
            <select
              value={selectedOwner}
              onChange={(e) => setSelectedOwner(e.target.value)}
              className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
            >
              <option value="">All Customers</option>
              {owners.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {!selectedOwner && (
            <div className="pvs-glass p-4">
              <label className="block text-sm text-pvs-text-muted mb-2">Leaderboard View</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setGroupByCompany(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !groupByCompany
                      ? 'bg-green-500/20 text-green-400 border border-green-400/40'
                      : 'pvs-glass pvs-glass-hover text-pvs-text-muted'
                  }`}
                >
                  Combined
                </button>
                <button
                  type="button"
                  onClick={() => setGroupByCompany(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    groupByCompany
                      ? 'bg-green-500/20 text-green-400 border border-green-400/40'
                      : 'pvs-glass pvs-glass-hover text-pvs-text-muted'
                  }`}
                >
                  By Company
                </button>
              </div>
            </div>
          )}

          {/* Period toggle */}
          <div className="flex gap-2 flex-wrap">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  period === p
                    ? 'bg-green-500/20 text-green-400 border border-green-400/40'
                    : 'pvs-glass pvs-glass-hover text-pvs-text-muted'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>

          {/* Date navigator */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setLeaderDate(shiftDate(leaderDate, period, -1))}
              className="pvs-glass pvs-glass-hover p-2 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="text-center flex-1">
              <p className="text-white font-medium">{formatDateLabel(leaderDate, period)}</p>
              <input
                type="date"
                value={leaderDate}
                onChange={(e) => e.target.value && setLeaderDate(e.target.value)}
                className="mt-1 bg-transparent border border-pvs-border rounded px-2 py-1 text-xs text-pvs-text-muted focus:outline-none focus:border-green-400/60"
              />
            </div>
            <button
              onClick={() => setLeaderDate(shiftDate(leaderDate, period, 1))}
              className="pvs-glass pvs-glass-hover p-2 rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Leaderboard tables */}
          {showGroupedByCompany ? (
            leaderLoading ? (
              <p className="text-pvs-text-muted text-sm text-center py-8">Loading...</p>
            ) : groupedCompanies.length === 0 ? (
              <p className="text-pvs-text-muted text-sm text-center py-8">No activity in this period</p>
            ) : (
              groupedCompanies.map((companyData) => (
                <CompanyLeaderboardSection
                  key={companyData.owner}
                  company={companyData.owner}
                  leaderboards={companyData.leaderboards}
                  loading={leaderLoading}
                />
              ))
            )
          ) : (
            <>
              <LeaderboardTable
                title="Tank Certifications"
                entries={leaderboard}
                loading={leaderLoading}
                emptyLabel="No certifications in this period"
                nameLabel="Examiner"
                countLabel="Certs"
              />
              <LeaderboardTable
                title="Pre-Inspections"
                entries={leaderboards.preInspection}
                loading={leaderLoading}
                emptyLabel="No pre-inspections in this period"
                nameLabel="Inspector"
                countLabel="Forms"
              />
              <LeaderboardTable
                title="Gas Free Checks"
                entries={leaderboards.gasFreeCheck}
                loading={leaderLoading}
                emptyLabel="No gas free checks in this period"
                nameLabel="Inspector"
                countLabel="Forms"
              />
              <LeaderboardTable
                title="Final QC"
                entries={leaderboards.finalQCChecklist}
                loading={leaderLoading}
                emptyLabel="No final QC checks in this period"
                nameLabel="Inspector"
                countLabel="Forms"
              />
            </>
          )}

          {/* Period totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label={`Total Tested ${PERIOD_SUFFIX[period]}`}
              value={leaderLoading ? '–' : periodTotals.tested}
              highlight
            />
            <StatCard
              label={`Total Painted ${PERIOD_SUFFIX[period]}`}
              value={leaderLoading ? '–' : periodTotals.painted}
            />
            <StatCard
              label={`Total Dispatched ${PERIOD_SUFFIX[period]}`}
              value={leaderLoading ? '–' : periodTotals.dispatched}
            />
          </div>

          {/* Report generator */}
          <div className="pvs-glass p-5 space-y-4">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6" stroke="url(#icon-two-tone)" />
              <div>
                <h2 className="text-lg font-semibold text-white">PDF Reports</h2>
                <p className="text-xs text-pvs-text-muted">
                  {isScrapReport
                    ? 'Calor Gas scrap figures for a Monday to Sunday week, with a list of every scrap vessel dispatched that week.'
                    : 'Weekly, monthly, quarterly or annual report with totals, size category breakdowns, scrap figures and a comparison with the previous two periods.'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-sm text-pvs-text-muted mb-1">Report</label>
                <select
                  value={reportKind}
                  onChange={(e) => setReportKind(e.target.value)}
                  className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                >
                  <option value="activity">Vessel Activity Report</option>
                  <option value="scrap">Weekly Scrap Report (Calor Gas only)</option>
                </select>
              </div>

              {!isScrapReport && (
                <>
                  <div>
                    <label className="block text-sm text-pvs-text-muted mb-1">Report Type</label>
                    <select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                    >
                      {REPORT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-pvs-text-muted mb-1">Report Scope</label>
                    <select
                      value={reportOwner}
                      onChange={(e) => setReportOwner(e.target.value)}
                      className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                    >
                      <option value="">Company-Wide (All Customers)</option>
                      {owners.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {effectiveReportType === 'weekly' && (
                <div className="sm:col-span-2">
                  <label className="block text-sm text-pvs-text-muted mb-1">Report Week (pick any day in the week)</label>
                  <input
                    type="date"
                    value={reportDate}
                    onChange={(e) => e.target.value && setReportDate(e.target.value)}
                    className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                  />
                  <p className="text-xs text-pvs-text-muted mt-1">Covers {reportWeekLabel(reportDate)}</p>
                </div>
              )}

              {effectiveReportType === 'monthly' && (
                <div className="sm:col-span-2">
                  <label className="block text-sm text-pvs-text-muted mb-1">Report Month</label>
                  <input
                    type="month"
                    value={reportMonth}
                    onChange={(e) => e.target.value && setReportMonth(e.target.value)}
                    className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                  />
                </div>
              )}

              {effectiveReportType === 'quarterly' && (
                <div>
                  <label className="block text-sm text-pvs-text-muted mb-1">Report Quarter</label>
                  <select
                    value={reportQuarter}
                    onChange={(e) => setReportQuarter(Number(e.target.value))}
                    className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                  >
                    <option value={1}>Q1 (Jan – Mar)</option>
                    <option value={2}>Q2 (Apr – Jun)</option>
                    <option value={3}>Q3 (Jul – Sep)</option>
                    <option value={4}>Q4 (Oct – Dec)</option>
                  </select>
                </div>
              )}

              {(effectiveReportType === 'quarterly' || effectiveReportType === 'annual') && (
                <div>
                  <label className="block text-sm text-pvs-text-muted mb-1">Report Year</label>
                  <select
                    value={reportYear}
                    onChange={(e) => setReportYear(Number(e.target.value))}
                    className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
                  >
                    {REPORT_YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={downloadReport}
                disabled={reportBusy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-green-500/20 text-green-400 border border-green-400/40 hover:bg-green-500/30 transition-colors disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                {reportBusy ? 'Working...' : 'Download PDF Report'}
              </button>
              <button
                onClick={emailReport}
                disabled={reportBusy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium pvs-glass pvs-glass-hover text-white disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                {reportBusy ? 'Working...' : 'Email to forms@pvslimited.com'}
              </button>
            </div>

            {reportMessage && (
              <p className={`text-sm ${reportMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                {reportMessage.text}
              </p>
            )}

            <p className="text-xs text-pvs-text-muted">
              Calor Gas reports are generated automatically and emailed to forms@pvslimited.com on the
              1st of every month covering the previous month. All other reports are generated manually here.
            </p>
          </div>
        </div>
      </PVSPageLayout>
    );
  }

  // Main overview
  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex justify-between items-center gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">Analytics</h1>
          <button
            className="flex items-center justify-center w-12 h-12 flex-shrink-0 pvs-glass pvs-glass-hover"
            onClick={onReturnHome}
            title="Return to Homepage"
          >
            <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
          </button>
        </div>

        {/* Customer filter */}
        <div className="pvs-glass p-4">
          <label className="block text-sm text-pvs-text-muted mb-2">Filter by Customer</label>
          <select
            value={selectedOwner}
            onChange={(e) => setSelectedOwner(e.target.value)}
            className="w-full bg-pvs-bg border border-pvs-border rounded-lg px-3 py-2 text-white focus:outline-none focus:border-green-400/60"
          >
            <option value="">All Customers</option>
            {owners.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        {/* Stock / status cards */}
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {loading ? (
          <div className="text-center text-pvs-text-muted py-12">Loading...</div>
        ) : stats && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <StatCard label="Total" value={stats.total} highlight />
              <StatCard label="Waiting Test" value={stats.waitingTest} />
              <StatCard label="Painted" value={stats.painted} />
              <StatCard label="Valved" value={stats.valved} />
              <StatCard label="Ready for Dispatch" value={stats.readyForDispatch} />
            </div>

            {/* Lead time */}
            <div className="pvs-glass p-5">
              <h2 className="text-lg font-semibold text-white mb-2">Average Lead Time</h2>
              <p className="text-3xl font-bold text-white">
                {stats.leadTime.avgWeeks !== null ? `${stats.leadTime.avgWeeks} weeks` : 'No data'}
              </p>
              <p className="text-xs text-pvs-text-muted mt-2">
                Pre-inspection to tank certification &middot; {stats.leadTime.sampleSize} vessels &middot; outliers excluded (IQR)
              </p>
            </div>
          </>
        )}

        {/* Admin button — navigates to leaderboard sub-page */}
        {isAdmin && (
          <button
            onClick={() => setView('admin')}
            className="w-full pvs-glass pvs-glass-hover flex items-center px-5 py-5 text-left"
          >
            <div className="flex items-center space-x-4 w-full">
              <div className="p-3 rounded-2xl flex-shrink-0"
                style={{
                  background: 'rgba(74, 222, 128, 0.06)',
                  border: '1px solid rgba(74, 222, 128, 0.15)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <ShieldCheck className="h-7 w-7" stroke="url(#icon-two-tone)" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Admin</h3>
                <p className="text-sm text-pvs-text-muted">Certification leaderboard &amp; performance</p>
              </div>
            </div>
          </button>
        )}

      </div>
    </PVSPageLayout>
  );
};

export default Analytics;
